<template>
  <div class="rich-text-editor">
    <!-- Mode Tabs -->
    <div class="editor-tabs">
      <button
        v-for="mode in modes"
        :key="mode.key"
        :class="['tab-button', { active: currentMode === mode.key }]"
        type="button"
        @click.prevent.stop="switchMode(mode.key as 'design' | 'html' | 'preview')"
      >
        {{ t(`richTextEditor.${mode.key}Mode`) }}
      </button>
    </div>

    <!-- Design Mode -->
    <div v-show="currentMode === 'design'" class="editor-container">
      <!-- Toolbar -->
      <div class="editor-toolbar">
        <div class="toolbar-group">
          <button
            v-for="action in formatActions"
            :key="action.command"
            :class="['toolbar-button', { active: isFormatActive(action.command) }]"
            :title="action.title"
            type="button"
            @click.prevent.stop="executeCommand(action.command)"
            @mousedown.prevent
          >
            <span v-html="action.icon"></span>
          </button>
        </div>

        <div class="toolbar-group">
          <select
            class="toolbar-select"
            @change.prevent.stop="executeCommand('formatBlock', ($event.target as HTMLSelectElement)?.value)"
            @mousedown.prevent
          >
            <option value="">{{ t('richTextEditor.normalText') }}</option>
            <option value="<h1>">{{ t('richTextEditor.heading1') }}</option>
            <option value="<h2>">{{ t('richTextEditor.heading2') }}</option>
            <option value="<h3>">{{ t('richTextEditor.heading3') }}</option>
            <option value="<p>">{{ t('richTextEditor.paragraph') }}</option>
          </select>
        </div>

        <div class="toolbar-group">
          <button
            v-for="listAction in listActions"
            :key="listAction.command"
            :class="['toolbar-button', { active: isFormatActive(listAction.command) }]"
            :title="listAction.title"
            type="button"
            @click.prevent.stop="executeCommand(listAction.command)"
            @mousedown.prevent
          >
            <span v-html="listAction.icon"></span>
          </button>
        </div>

        <div class="toolbar-group">
          <input
            type="color"
            class="color-picker"
            :title="t('richTextEditor.textColor')"
            @change.prevent.stop="executeCommand('foreColor', ($event.target as HTMLInputElement)?.value)"
          />
          <input
            type="color"
            class="color-picker"
            :title="t('richTextEditor.backgroundColor')"
            @change.prevent.stop="executeCommand('backColor', ($event.target as HTMLInputElement)?.value)"
          />
        </div>

        <div class="toolbar-group">
          <button
            class="toolbar-button"
            type="button"
            :title="t('richTextEditor.createLink')"
            @click.prevent.stop="createLink"
            @mousedown.prevent
          >
            🔗
          </button>
          <button
            class="toolbar-button"
            type="button"
            :title="t('richTextEditor.removeLink')"
            @click.prevent.stop="executeCommand('unlink')"
            @mousedown.prevent
          >
            🔗❌
          </button>
        </div>
      </div>

      <!-- Editor Content -->
      <div
        ref="editorRef"
        class="editor-content"
        :class="{ 'has-error': hasError }"
        contenteditable="true"
        :style="{ height: height }"
        dir="ltr"
        @input="handleInput"
        @paste="handlePaste"
        @keydown="handleKeydown"
      ></div>
    </div>

    <!-- HTML Mode -->
    <div v-show="currentMode === 'html'" class="editor-container">
      <textarea
        ref="htmlTextareaRef"
        v-model="htmlContent"
        class="html-editor"
        :class="{ 'has-error': hasError }"
        :style="{ height: height }"
        :placeholder="t('richTextEditor.htmlPlaceholder')"
        @input="handleHtmlInput"
      ></textarea>
    </div>

    <!-- Preview Mode -->
    <div v-show="currentMode === 'preview'" class="editor-container">
      <div
        class="preview-content"
        :style="{ height: height }"
        v-html="sanitizedHtml"
      ></div>
    </div>

    <!-- Error Message -->
    <div v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  modelValue?: string
  height?: string
  placeholder?: string
  hasError?: boolean
  errorMessage?: string
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'change', value: string): void
  (e: 'manual-change', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  height: '300px',
  placeholder: '',
  hasError: false,
  errorMessage: ''
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

// Refs
const editorRef = ref<HTMLDivElement>()
const htmlTextareaRef = ref<HTMLTextAreaElement>()

// State
const currentMode = ref<'design' | 'html' | 'preview'>('design')
const htmlContent = ref(props.modelValue)
const forceToolbarUpdate = ref(false)

// Mode configuration
const modes = [
  { key: 'design', labelKey: 'richTextEditor.Design', label: t('richTextEditor.Design') },
  { key: 'html', labelKey: 'richTextEditor.HTML', label: t('richTextEditor.HTML') },
  { key: 'preview', labelKey: 'richTextEditor.Preview', label: t('richTextEditor.Preview') }
]

// Toolbar actions
const formatActions = [
  { command: 'bold', icon: '<strong>B</strong>', title: t('richTextEditor.bold') },
  { command: 'italic', icon: '<em>I</em>', title: t('richTextEditor.italic') },
  { command: 'underline', icon: '<u>U</u>', title: t('richTextEditor.underline') },
  { command: 'strikeThrough', icon: '<s>S</s>', title: t('richTextEditor.strikeThrough') },
  { command: 'justifyLeft', icon: '⬅️', title: t('richTextEditor.alignLeft') },
  { command: 'justifyCenter', icon: '↔️', title: t('richTextEditor.alignCenter') },
  { command: 'justifyRight', icon: '➡️', title: t('richTextEditor.alignRight') },
  { command: 'justifyFull', icon: '↕️', title: t('richTextEditor.justify') }
]

const listActions = [
  { command: 'insertUnorderedList', icon: '• • •', title: t('richTextEditor.bulletList') },
  { command: 'insertOrderedList', icon: '1. 2. 3.', title: t('richTextEditor.numberedList') },
  { command: 'outdent', icon: '⬅', title: t('richTextEditor.decreaseIndent') },
  { command: 'indent', icon: '➡', title: t('richTextEditor.increaseIndent') }
]

// Computed
const sanitizedHtml = computed(() => {
  // Basic HTML sanitization to prevent XSS
  const div = document.createElement('div')
  div.innerHTML = htmlContent.value

  // Remove script tags and dangerous attributes
  const scripts = div.querySelectorAll('script')
  scripts.forEach(script => script.remove())

  const elements = div.querySelectorAll('*')
  elements.forEach(el => {
    // Remove dangerous attributes
    const dangerousAttrs = ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur']
    dangerousAttrs.forEach(attr => {
      if (el.hasAttribute(attr)) {
        el.removeAttribute(attr)
      }
    })
  })

  return div.innerHTML
})

// Methods
const switchMode = (mode: 'design' | 'html' | 'preview') => {
  console.log('🔄 switchMode called:', mode)

  if (currentMode.value === 'design' && editorRef.value) {
    htmlContent.value = editorRef.value.innerHTML
  }

  currentMode.value = mode

  if (mode === 'design') {
    nextTick(() => {
      if (editorRef.value) {
        editorRef.value.innerHTML = htmlContent.value
      }
    })
  }

  console.log('🔄 switchMode completed, NOT emitting events')
}

const executeCommand = (command: string, value?: string) => {
  console.log('🔧 executeCommand called:', command, value)

  if (editorRef.value) {
    // Prevent any form submission during command execution
    const form = editorRef.value.closest('form')
    if (form) {
      const originalOnSubmit = form.onsubmit
      form.onsubmit = (e) => {
        console.log('🚫 Blocked form submit during executeCommand')
        e.preventDefault()
        return false
      }

      // Execute the command
      document.execCommand(command, false, value)

      // Restore original form handler after a delay
      setTimeout(() => {
        form.onsubmit = originalOnSubmit
      }, 100)
    } else {
      document.execCommand(command, false, value)
    }

    // Update internal state
    htmlContent.value = editorRef.value.innerHTML
    console.log('🔧 executeCommand - NOT emitting update:modelValue')

    // Force immediate update of toolbar button states
    forceToolbarUpdate.value = !forceToolbarUpdate.value

    // Also trigger after a small delay to ensure the command has taken effect
    setTimeout(() => {
      forceToolbarUpdate.value = !forceToolbarUpdate.value
    }, 10)
  }
}

const isFormatActive = (command: string): boolean => {
  // Use forceToolbarUpdate to trigger reactivity
  forceToolbarUpdate.value
  try {
    return document.queryCommandState(command)
  } catch {
    return false
  }
}

const createLink = () => {
  const url = prompt(t('richTextEditor.enterUrl'))
  if (url) {
    executeCommand('createLink', url)
  }
}

const handleInput = (event: Event) => {
  console.log('⌨️ handleInput called, event type:', event.type)
  if (editorRef.value && event.target === editorRef.value) {
    htmlContent.value = editorRef.value.innerHTML
    console.log('⌨️ handleInput - emitting update:modelValue and manual-change')
    emit('update:modelValue', htmlContent.value)
    // Clear any existing error when user starts typing
    emit('manual-change', htmlContent.value)
  }
}

const handleHtmlInput = () => {
  emit('update:modelValue', htmlContent.value)
  emit('manual-change', htmlContent.value)
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const text = event.clipboardData?.getData('text/plain') || ''
  document.execCommand('insertText', false, text)
}

const handleKeydown = (event: KeyboardEvent) => {
  // Handle common keyboard shortcuts
  if (event.ctrlKey || event.metaKey) {
    switch (event.key.toLowerCase()) {
      case 'b':
        event.preventDefault()
        executeCommand('bold')
        break
      case 'i':
        event.preventDefault()
        executeCommand('italic')
        break
      case 'u':
        event.preventDefault()
        executeCommand('underline')
        break
    }
  }
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue !== htmlContent.value) {
    htmlContent.value = newValue
    if (currentMode.value === 'design' && editorRef.value) {
      editorRef.value.innerHTML = newValue
    }
  }
})

// Lifecycle
onMounted(() => {
  if (editorRef.value) {
    editorRef.value.innerHTML = props.modelValue || ''

    // Listen for selection changes to update toolbar states
    document.addEventListener('selectionchange', updateToolbarStates)
    editorRef.value.addEventListener('keyup', updateToolbarStates)
    editorRef.value.addEventListener('mouseup', updateToolbarStates)
  }
})

// Update toolbar button states
const updateToolbarStates = () => {
  // Force Vue to re-evaluate isFormatActive for all buttons
  // This is a reactive update that will make buttons show active state immediately
}

// Initialize content without v-html to prevent cursor issues
watch(() => props.modelValue, (newValue) => {
  if (newValue !== htmlContent.value && editorRef.value && currentMode.value === 'design') {
    // Only update if the content is different and we're not currently editing
    const selection = window.getSelection()
    const isEditorFocused = document.activeElement === editorRef.value

    if (!isEditorFocused) {
      editorRef.value.innerHTML = newValue
      htmlContent.value = newValue
    }
  }
}, { immediate: true })

// Cleanup event listeners
onBeforeUnmount(() => {
  document.removeEventListener('selectionchange', updateToolbarStates)
  if (editorRef.value) {
    editorRef.value.removeEventListener('keyup', updateToolbarStates)
    editorRef.value.removeEventListener('mouseup', updateToolbarStates)
  }
})
</script>

<style lang="scss" scoped>
.rich-text-editor {
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  overflow: hidden;
  background: white;
}

.editor-tabs {
  display: flex;
  background: var(--vt-c-white-soft);
  border-bottom: 1px solid #dee2e6;
}

.tab-button {
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  @include typography-body;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.15s ease;
  border-bottom: 2px solid transparent;
}

.tab-button:hover {
  background: #e9ecef;
  color: #495057;
}

.tab-button.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background: white;
}

.editor-container {
  position: relative;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--vt-c-white-soft);
  border-bottom: 1px solid #dee2e6;
  align-items: center;
}

.toolbar-group {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.toolbar-button {
  padding: 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  background: white;
  cursor: pointer;
  @include typography-small;
  border-radius: $radius-sm;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.toolbar-button:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.toolbar-button.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.toolbar-select {
  padding: 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  @include typography-small;
  background: white;
  height: 32px;
}

.color-picker {
  width: 32px;
  height: 32px;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  cursor: pointer;
  padding: 0;
}

.editor-content {
  padding: 1rem;
  min-height: 200px;
  outline: none;
  overflow-y: auto;
  @include typography-body;
  line-height: 1.5;
  font-family: inherit;
}

.editor-content:focus {
  box-shadow: inset 0 0 0 1px #007bff;
}

.html-editor {
  width: 100%;
  padding: 1rem;
  border: none;
  outline: none;
  resize: none;
  @include typography-small;
  font-family: 'Courier New', monospace;
  line-height: 1.4;
  background: var(--vt-c-white-soft);
}

.html-editor:focus {
  box-shadow: inset 0 0 0 1px #007bff;
}

.preview-content {
  padding: 1rem;
  overflow-y: auto;
  @include typography-body;
  line-height: 1.5;
  font-family: inherit;
  background: var(--vt-c-white-soft);
}

.has-error {
  border-color: #dc3545 !important;
}

.has-error:focus {
  box-shadow: inset 0 0 0 1px #dc3545 !important;
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

/* Content styling */
.editor-content h1,
.preview-content h1 {
  @include typography-h3;
  @include font-weight-bold;
  margin: 0.5rem 0;
}

.editor-content h2,
.preview-content h2 {
  @include typography-h4;
  @include font-weight-bold;
  margin: 0.5rem 0;
}

.editor-content h3,
.preview-content h3 {
  @include typography-body-large;
  @include font-weight-bold;
  margin: 0.5rem 0;
}

.editor-content p,
.preview-content p {
  margin: 0.5rem 0;
}

.editor-content ul,
.editor-content ol,
.preview-content ul,
.preview-content ol {
  margin: 0.5rem 0;
  padding-left: 2rem;
}

.editor-content a,
.preview-content a {
  color: #007bff;
  text-decoration: underline;
}

.editor-content a:hover,
.preview-content a:hover {
  color: #0056b3;
}

/* Responsive design */
@media (max-width: 768px) {
  .editor-toolbar {
    gap: $gap-xs;
    padding: $spacing-1;
  }

  .toolbar-group {
    flex-wrap: wrap;
    gap: $gap-xs;
  }

  .toolbar-select {
    flex: 1;
  }

  .editor-tabs {
    flex-wrap: wrap;
  }

  .tab-button {
    flex: 1;
    min-width: 80px;
  }
}
</style>
